import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

// Providers / utilities
import ScrollToTopNav from "./components/scroll-to-top/scroll-to-top-nav";
import { ScrollProvider } from "./context/scroll-context";
import { AppProvider } from "./context/app-context";
import { Helmet } from "react-helmet-async";

// Layouts / screens
import LoadingScreen from "./components/loader/loading-screen";
import ErrorPageLayout from "./layouts/error-layout";
import routeMetadata from "./route-metadata";
import PrivacyPolicy from "./pages/privacy-policy";

// Route configs
import { servicesRoutes, solutionsRoutes, hireTeamRoutes, portfolioRoutes } from "./routes-config";
import BlogComponent from "./pages/blog/blog-home";
import BlogLayout from "./pages/blog/blog-layout";
import AllPosts from "./pages/blog/all-posts";
import CategoryPage from "./pages/blog/category-page";

// Lazy pages/layouts
const HomePage = lazy(() => import("./pages/home/home"));
const DefaultLayout = lazy(() => import("./layouts/default-layout"));
const ContactUs = lazy(() => import("./pages/contact-us/contact-us"));
const Portfolio = lazy(() => import("./pages/portfolio/portfolio"));
const NotFound = lazy(() => import("./pages/not-found"));
const ServiceBaseLayout = lazy(() => import("./pages/services/subpages/base-layout"));
const SolutionsBaseLayout = lazy(() => import("./pages/solutions/subpages/base-layout"));
const HireTeamBaseLayout = lazy(() => import("./pages/hire-team/subpages/base-layout"));
const PortfolioBaseLayout = lazy(() => import("./pages/portfolio/subpages/base-layout"));

function App() {
  const location = useLocation();
  const { pathname, search } = location; // ✅ fixed (include search)

  // ---- SEO Meta by route ----
  const metadata = routeMetadata[pathname] || routeMetadata["/"] || {};
  const { title = "", description = "", keywords = "", twitterCard } = metadata;

  const canonicalUrl = `https://acubetechnologies.com${pathname}${search || ""}`;

  // ---- Google Analytics / Ads page tracking ----
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("config", "AW-16685554601", {
        page_path: pathname + search,
      });
    }
  }, [pathname, search]);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY} key={pathname}>
      <ScrollProvider>
        <AppProvider>
          <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />

            {/* Twitter */}
            <meta name="twitter:card" content={twitterCard || "summary_large_image"} />
            <meta name="twitter:title" content={metadata.title || title} />
            <meta name="twitter:description" content={metadata.description || description} />

            {/* Open Graph */}
            <meta property="og:title" content={metadata.title || title} />
            <meta property="og:description" content={metadata.description || description} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:type" content="website" />

            <link rel="canonical" href={canonicalUrl} />
          </Helmet>

          <ScrollToTopNav />

          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route element={<DefaultLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/portfolio" element={<Portfolio />} />

                {servicesRoutes.map(({ path, pageName }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<ServiceBaseLayout pageName={pageName} />}
                  />
                ))}

                {solutionsRoutes.map(({ path, pageName }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<SolutionsBaseLayout pageName={pageName} />}
                  />
                ))}

                {hireTeamRoutes.map(({ path, pageName }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<HireTeamBaseLayout pageName={pageName} />}
                  />
                ))}

                {portfolioRoutes.map(({ path, pageName }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<PortfolioBaseLayout pageName={pageName} />}
                  />
                ))}

                <Route
                  exact={true}
                  path="/blogs"
                  element={<BlogComponent />}
                />
                <Route
                  exact={true}
                  path="/blogs/:slug"
                  element={<BlogLayout />}
                />
                <Route
                  path="/blogs/viewall/:type"
                  element={<AllPosts />}
                />
                <Route
                  path="/blogs/category/:category"
                  element={<CategoryPage />}
                />

                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              </Route>

              <Route element={<ErrorPageLayout />}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </AppProvider>
      </ScrollProvider>
    </GoogleReCaptchaProvider>
  );
}

export default App;
